import { Link } from 'react-router-dom';
import { useConfig } from '@/core/config/ConfigProvider';
import { cx } from '@/design-system';

/**
 * A MARCA, EM UM LUGAR SÓ
 *
 * Cabeçalho, rodapé, telas de entrar/cadastrar/convite e o painel desenham a
 * mesma marca. Antes disso, quem sabia trocar o logotipo pelo publicado era o
 * cabeçalho e mais ninguém: o rodapé repetia o mesmo bloco por acaso (era o
 * mesmo componente interno) e as telas de conta não tinham marca nenhuma —
 * justamente as telas em que reconhecer o portal mais importa, porque são as
 * que pedem senha.
 *
 * Publicar um logotipo novo agora troca em todos ao mesmo tempo. Era o pedido:
 * onde a marca aparece, o painel manda.
 *
 * COMO O LOGOTIPO ESCURO FUNCIONA
 *
 * As duas imagens são renderizadas, e QUEM DECIDE É O CSS — não o JavaScript.
 * O tema aqui tem três estados, não dois: `data-theme="light"`,
 * `data-theme="dark"`, e o padrão sem marca nenhuma, em que só o
 * `prefers-color-scheme` separa um do outro. Ler isso em JavaScript daria
 * troca piscando na primeira pintura e obrigaria a assinar mudança de tema do
 * sistema operacional; três regras de CSS resolvem nos três estados. As regras
 * moram em `theme.css`, ao lado das outras do tema.
 *
 * Sem logotipo publicado, o retorno é o nome com um selo — o que o portal já
 * fazia, e continua sendo o padrão de fábrica.
 */

type Props = {
  /** Altura do logotipo. O padrão serve ao cabeçalho. */
  className?: string;

  /**
   * Envolve num link para a home.
   *
   * Desligado no login e no convite: ali a marca é identificação, não
   * navegação, e um link para fora no meio de um formulário de senha é
   * caminho para abandonar o cadastro sem querer.
   */
  asLink?: boolean;
};

export function Marca({ className, asLink = true }: Props) {
  const { site } = useConfig();
  const { name, logoUrl, logoDarkUrl } = site.brand;

  const conteudo = logoUrl ? (
    <>
      <img
        src={logoUrl}
        alt={name}
        className={cx('w-auto object-contain', logoDarkUrl ? 'marca-clara' : '', className ?? 'h-8')}
      />

      {logoDarkUrl ? (
        <img
          src={logoDarkUrl}
          alt={name}
          className={cx('marca-escura w-auto object-contain', className ?? 'h-8')}
        />
      ) : null}
    </>
  ) : (
    <>
      <span
        aria-hidden="true"
        className="grid h-9 w-9 place-items-center rounded-lg bg-primary font-display text-lg font-extrabold text-primary-fg"
      >
        {name.charAt(0)}
      </span>
      <span className="font-display text-xl font-extrabold tracking-tight text-ink">{name}</span>
    </>
  );

  if (!asLink) {
    return (
      <span className="flex shrink-0 items-center gap-2.5" aria-label={name}>
        {conteudo}
      </span>
    );
  }

  return (
    <Link
      to="/"
      className="flex shrink-0 items-center gap-2.5 no-underline"
      aria-label={`${name} — início`}
    >
      {conteudo}
    </Link>
  );
}
